
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface ServiceCardProps {
  title: string;
  description: string | React.ReactNode;
  icon: React.ReactNode;
  link?: string; 
  delay?: number;
  image?: string;
  benefits?: string[];
  specialNote?: string;
  hideLink?: boolean;
  buttonText?: string;
  buttonUrl?: string;
}

const ServiceCard = ({
  title,
  description,
  icon,
  link,
  delay = 0,
  image,
  benefits,
  specialNote,
  hideLink = false,
  buttonText,
  buttonUrl
}: ServiceCardProps) => {
  // Determine if we have a link to make the card clickable
  const hasLink = !hideLink && (link || buttonUrl);
  
  // Create the card content as a separate component for clean JSX
  const CardContent = () => (
    <>
      <div className="flex flex-col items-center mb-3">
        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary mb-2 transition-transform duration-300 group-hover:scale-110">
          {icon}
        </div>
        <h3 className="text-xl sm:text-xl md:text-2xl font-semibold text-center mb-2 transition-colors duration-300 group-hover:text-primary">
          {title}
        </h3>
      </div>
      
      <p className="text-joerg-600 sm:text-base text-left">
        {description}
      </p>
      
      {benefits && benefits.length > 0 && (
        <ul className="mt-3 sm:mt-4 space-y-2 flex-grow">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-start gap-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span className="text-joerg-600 text-sm sm:text-base">{benefit}</span>
            </li>
          ))}
        </ul>
      )}
      
      {!benefits && <div className="flex-grow mt-3"></div>}
      
      {image && (
        <div className="mt-4 sm:mt-5 mb-4 overflow-hidden rounded-lg">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            width="400"
            height="300"
            decoding="async"
          />
        </div>
      )}
      
      {specialNote && (
        <div className="mt-3 p-3 bg-primary/5 border border-primary/10 rounded-lg text-sm sm:text-base italic text-joerg-600 text-left">
          {specialNote}
        </div>
      )}
    </>
  );

  // If the card has no link, render it as a simple div
  if (!hasLink) {
    return (
      <div 
        className="bg-white p-5 sm:p-6 md:p-8 rounded-2xl shadow-soft h-full flex flex-col transition-all duration-500 animate-fade-in card-hover-effect" 
        style={{
          animationDelay: `${delay}ms`,
          opacity: 0,
          animation: `fade-in 0.6s ease-out ${delay}ms forwards`
        }}
      >
        <CardContent />
      </div>
    );
  }

  // If it's an external URL (button URL)
  if (buttonUrl) {
    return (
      <a
        href={buttonUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-white p-5 sm:p-6 md:p-8 rounded-2xl shadow-soft h-full flex flex-col transition-all duration-500 animate-fade-in hover:shadow-elevation group cursor-pointer active:bg-gray-50 touch-active relative card-hover-effect" 
        style={{
          animationDelay: `${delay}ms`,
          opacity: 0,
          animation: `fade-in 0.6s ease-out ${delay}ms forwards`
        }}
        aria-label={`Mehr über ${title} erfahren`}
      >
        {/* Mobile highlight border - visible on all devices */}
        <div className="absolute inset-0 border-2 border-primary/0 rounded-2xl transition-opacity duration-300 group-hover:border-primary/30 pointer-events-none card-highlight-border"></div>
        <CardContent />
      </a>
    );
  }

  // Otherwise it's an internal link
  return (
    <Link
      to={link || "#"}
      className="bg-white p-5 sm:p-6 md:p-8 rounded-2xl shadow-soft h-full flex flex-col transition-all duration-500 animate-fade-in hover:shadow-elevation group cursor-pointer active:bg-gray-50 touch-active relative card-hover-effect" 
      style={{
        animationDelay: `${delay}ms`,
        opacity: 0,
        animation: `fade-in 0.6s ease-out ${delay}ms forwards`
      }}
      aria-label={`Mehr über ${title} erfahren`}
    >
      {/* Mobile highlight border - visible on all devices */}
      <div className="absolute inset-0 border-2 border-primary/0 rounded-2xl transition-opacity duration-300 group-hover:border-primary/30 pointer-events-none card-highlight-border"></div>
      <CardContent />
    </Link>
  );
};

export default ServiceCard;
